import { useEffect, useRef, useState } from 'react';
import { t } from 'utilities/TranslationUtils';
import PlayerAvatar from 'components/ui/PlayerAvatar';

export interface SessionPlayer {
  id: string;
  name: string;
  avatarUrl?: string | null;
}

interface SessionPlayerPickerProps {
  players: SessionPlayer[];
  selectedId: string | null;
  onSelect: (playerId: string) => void;
}

/** Avatar + name dropdown for the session's players. Disabled (plain label) when there is only one player. */
const SessionPlayerPicker: React.FunctionComponent<SessionPlayerPickerProps> = ({ players, selectedId, onSelect }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = players.find((p) => p.id === selectedId) ?? players[0];
  const disabled = players.length <= 1;

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!selected) return null;
  const label = (p: SessionPlayer) => p.name || t('measurementDetail.unknownPlayer');

  return (
    <div className='session-player-picker' ref={rootRef} data-test='session-player-picker'>
      <button
        type='button'
        className='session-player-picker__button'
        aria-label={t('measurementDetail.player')}
        aria-haspopup='listbox'
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        data-test='session-player-picker-button'
      >
        <PlayerAvatar name={label(selected)} imageUrl={selected.avatarUrl} size='md' />
        <span className='session-player-picker__name'>{label(selected)}</span>
        {!disabled && <span className='session-player-picker__chevron' aria-hidden='true' />}
      </button>
      {open && (
        <ul className='session-player-picker__menu' role='listbox' data-test='session-player-picker-menu'>
          {players.map((p) => (
            <li key={p.id} role='option' aria-selected={p.id === selected.id}>
              <button
                type='button'
                className={`session-player-picker__option${p.id === selected.id ? ' is-selected' : ''}`}
                onClick={() => { setOpen(false); if (p.id !== selected.id) onSelect(p.id); }}
              >
                <PlayerAvatar name={label(p)} imageUrl={p.avatarUrl} size='sm' />
                <span>{label(p)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SessionPlayerPicker;
